<script setup lang="ts">
import type { FFDragEvent } from '@/types/Firefox/DragEvent'
import { type Position } from '@vueuse/core'
import { ref } from 'vue'

type Props = {
    setDragPosition?: boolean
}
const props = defineProps<Props>()

type Emits = {
    (e: 'dragStart', event: FFDragEvent | TouchEvent): void
    (e: 'dragEnd', position?: Position, event?: FFDragEvent | TouchEvent): void
    (e: 'drag', position?: Position): void
}
const emit = defineEmits<Emits>()

const handle = ref<HTMLDivElement | null>(null)

const mouseDown = (event: any) => {
    event.stopPropagation()

    if (event.dataTransfer) event.dataTransfer.effectAllowed = 'move'

    const hideDragImage = event.currentTarget?.cloneNode(true)
    hideDragImage.id = 'hidedraggeimage'
    const dragImage = event.currentTarget?.cloneNode(true)
    dragImage.id = 'draggeimage'

    dragImage.style.pointerEvents = 'none'
    dragImage.style.position = 'absolute'
    dragImage.style.cursor = 'grabbing'
    dragImage.style.visibility = 'hidden'
    dragImage.style.transform = 'translate(-50%, -50%)'
    hideDragImage.style.opacity = 0
    hideDragImage.style.cursor = 'grabbing'

    document.body.appendChild(hideDragImage)
    document.body.appendChild(dragImage)

    event.dataTransfer?.setDragImage(hideDragImage, event.x, event.y)

    emit('dragStart', event)
}

const mouseUp = (event: FFDragEvent | TouchEvent) => {
    const dragImage = document.getElementById('draggeimage')
    const hideDragImage = document.getElementById('hidedraggeimage')
    if (dragImage && handle.value) {
        handle.value.style.position = 'relative'
        handle.value.style.removeProperty('visibility')
        handle.value.style.removeProperty('left')
        handle.value.style.removeProperty('top')
    }
    hideDragImage?.remove()
    dragImage?.remove()
    emit(
        'dragEnd',
        {
            x:
                (event as FFDragEvent)._ffix_cx ??
                (event as FFDragEvent).x ??
                (event as TouchEvent).changedTouches?.[0]?.clientX,
            y:
                (event as FFDragEvent)._ffix_cy ??
                (event as FFDragEvent).y ??
                (event as TouchEvent).changedTouches?.[0]?.clientY
        },
        event
    )
}

const setPosition = (event: FFDragEvent | TouchEvent) => {
    const dragImage = document.getElementById('draggeimage')
    if (dragImage && handle.value) {
        dragImage.style.removeProperty('visibility')
        dragImage.style.left = `${
            (event as DragEvent).x ?? (event as TouchEvent).touches?.[0]?.clientX
        }px`
        dragImage.style.top = `${
            (event as DragEvent).y ?? (event as TouchEvent).touches?.[0]?.clientY
        }px`
        handle.value.style.visibility = 'hidden'
    }
    if (props.setDragPosition)
        emit('drag', {
            x:
                (event as FFDragEvent)._ffix_cx ??
                (event as FFDragEvent).x ??
                (event as TouchEvent).changedTouches?.[0]?.clientX,
            y:
                (event as FFDragEvent)._ffix_cy ??
                (event as FFDragEvent).y ??
                (event as TouchEvent).changedTouches?.[0]?.clientY
        })
}

defineExpose({ handle })
</script>

<template>
    <div
        ref="handle"
        :draggable="true"
        @dragstart.passive="mouseDown"
        @dragend.passive="mouseUp"
        @drag.passive="setPosition"
        @touchstart.passive="mouseDown"
        @touchend.passive="mouseUp"
        @touchmove.passive="setPosition"
        class="drag-card"
        v-bind="$attrs">
        <slot v-bind="$attrs" />
    </div>
</template>
